import cloneDeep from 'lodash/fp/cloneDeep';
import businessService from '../service/businessService';
import i18n from '../i18n';
import router from '../router';
import log from '../service/log';
import { AvrProducts, ProductFamilyMap, AvrProductDetailsDisplayNames } from '@/utils/ProductConstants';
import variants from '@/utils/enums/alertVariants';

// Products hidden from the list when a higher tier product is enabled:
// Inventory Matching supersedes Pre-qualify and Credit+, and Pre-qualify supersedes Credit+
const SupersededProducts = {
  [AvrProducts.INVENTORY_MATCHING]: [AvrProducts.PREQUALIFY, AvrProducts.CREDIT],
  [AvrProducts.PREQUALIFY]: [AvrProducts.CREDIT],
};

// Comma separated display names of the enabled products in avrProductDetails, e.g. "Credit, Trade, Pre-qualify"
const getActiveProductList = avrProductDetails => {
  const enabledProducts = Object.keys(avrProductDetails || {}).filter(product => avrProductDetails[product]?.isEnabled);
  const hiddenProducts = enabledProducts.flatMap(product => SupersededProducts[product] || []);

  return enabledProducts
    .filter(product => !hiddenProducts.includes(product))
    .map(product => AvrProductDetailsDisplayNames[product] || product)
    .join(', ');
};

const initialState = {
  /* The business that is currently selected in the dropdown */
  currentBusiness: null,
  /* The currently selected integration from activeIntegrations */
  currentIntegration: null,
  /* All the businesses that are associated with the user (Business dropdown at the top) */
  userBusinesses: [],
  users: [],
  currentBusinessCSRepInfo: null,
  hardlinks: [],
};

const getters = {
  getUserById: state => id => state.users.find(user => user.id === id),
  currentBusinessId: state => (state.currentBusiness && state.currentBusiness.id ? state.currentBusiness.id : null),
  currentBusinessName: state =>
    state.currentBusiness && state.currentBusiness.name ? state.currentBusiness.name : null,
  currentBusinessCrmId: state =>
    state.currentBusiness && state.currentBusiness.crmId ? state.currentBusiness.crmId : null,
  currentBusinessProducts: state => {
    const products = [];
    // Todo: get this reviews data from the avrProductDetails field in future PR
    if (state.currentBusiness && state.currentBusiness.reviews) {
      products.push('reviews');
    }
    return products;
  },
  // IMPORTANT: in the domain of AV Dashboard, not all AVR products are treated as AVR products,
  // so we use the productFamilyMap to build this differentiation
  currentBusinessEnabledAvrProducts: state => {
    const products = [];
    if (state.currentBusiness && state.currentBusiness.avrProductDetails) {
      ProductFamilyMap[AvrProducts.AVR].forEach(productName => {
        if (
          state.currentBusiness.avrProductDetails[productName] &&
          state.currentBusiness.avrProductDetails[productName].isEnabled
        ) {
          products.push(productName);
        }
      });
    }
    return products;
  },
  // eslint-disable-next-line
  currentBusinessDisabledAvrProducts: (state, getters) => {
    // find which avr products are missing from the enabled products
    return ProductFamilyMap[AvrProducts.AVR].filter(
      product => !getters.currentBusinessEnabledAvrProducts.includes(product)
    );
  },
  currentBusinessEnabledStandaloneProducts: state => {
    const products = [];
    if (state.currentBusiness && state.currentBusiness.avrProductDetails) {
      ProductFamilyMap.standalone.forEach(productName => {
        if (
          state.currentBusiness.avrProductDetails[productName] &&
          state.currentBusiness.avrProductDetails[productName].isEnabled
        ) {
          products.push(productName);
        }
      });
      if (state.hardlinks.identityVerification) {
        products.push(AvrProducts.IDV);
      }
      if (state.hardlinks.credit) {
        products.push(AvrProducts.CREDIT);
      }
    }
    return products;
  },
  // eslint-disable-next-line
  currentBusinessDisabledStandaloneProducts: (state, getters) => {
    // find which standalone products are missing from the enabled products
    return ProductFamilyMap.standalone.filter(
      product => !getters.currentBusinessEnabledStandaloneProducts.includes(product)
    );
  },
  currentBusinessHasECommerce: state => {
    let hasEcommerce = false;
    if (state.currentBusiness && state.currentBusiness.avrProductDetails) {
      if (
        state.currentBusiness.avrProductDetails[AvrProducts.ECOMV2] &&
        state.currentBusiness.avrProductDetails[AvrProducts.ECOMV2].isEnabled
      ) {
        hasEcommerce = true;
      }
    }
    return hasEcommerce;
  },
  currentBusinessHasAVRExcludingECommerce: state => {
    let hasAVR = false;
    if (state.currentBusiness && state.currentBusiness.avrProductDetails) {
      ProductFamilyMap.avrAnalytics.forEach(productName => {
        if (
          state.currentBusiness.avrProductDetails[productName] &&
          state.currentBusiness.avrProductDetails[productName].isEnabled
        ) {
          hasAVR = true;
        }
      });
    }
    return hasAVR;
  },
  currentBusinessHasAIAssistant: state => {
    let hasAIAssistant = false;
    if (state.currentBusiness && state.currentBusiness.avrProductDetails) {
      if (
        state.currentBusiness.avrProductDetails[AvrProducts.AI_ASSISTANT] &&
        state.currentBusiness.avrProductDetails[AvrProducts.AI_ASSISTANT].isEnabled
      ) {
        hasAIAssistant = true;
      }
    }
    return hasAIAssistant;
  },
  currentBusinessIsMarketplace: state =>
    state.currentBusiness && state.currentBusiness.isMarketplace ? state.currentBusiness.isMarketplace : false,
  // eslint-disable-next-line no-shadow
  currentBusinessMarketplaceIntegrations: (state, getters) =>
    getters.currentBusinessIsMarketplace && state.currentBusiness.marketplace
      ? state.currentBusiness.marketplace.integrations
      : null,
};

const actions = {
  async setCurrentBusiness({ commit }, id) {
    try {
      const res = await businessService.getBusinessWithId(id);
      commit('SET_CURRENT_BUSINESS', res.data);
      commit('user/SET_CURRENT_BUSINESS_USER_ROLE', res.data.role, { root: true });
      // Remove the annoying NavigationDuplicated error
      router.push('/dashboard').catch(() => {});
    } catch (error) {
      log(error);
      commit(
        'SET_MESSAGE',
        {
          message: { msgHasLink: false, msg1: i18n.global.t('alerts.fetchingBusinessError') },
          variant: variants.DANGER,
        },
        { root: true }
      );
    }
  },

  async updateCurrentBusiness({ commit }, id) {
    try {
      const res = await businessService.getBusinessWithId(id);
      commit('SET_CURRENT_BUSINESS', res.data);
      commit('user/SET_CURRENT_BUSINESS_USER_ROLE', res.data.role, { root: true });
    } catch (error) {
      log(error);
      commit(
        'SET_MESSAGE',
        {
          message: { msgHasLink: false, msg1: i18n.global.t('alerts.fetchingBusinessError') },
          variant: variants.DANGER,
        },
        { root: true }
      );
    }
  },

  async getBusinessUsers({ commit }) {
    try {
      const res = await businessService.getBusinessUsers();
      commit('SET_BUSINESS_USERS', res.data);
    } catch (e) {
      commit(
        'SET_MESSAGE',
        { variant: variants.DANGER, message: { msg1: i18n.global.t('alerts.businessUserError'), msgHasLink: false } },
        { root: true }
      );
      log(e);
    }
  },

  async deleteSelectedBusinessUser({ commit }, userId) {
    try {
      commit('SET_LOADING', true, { root: true });
      await businessService.deleteSelectedBusinessUser(userId);
      router.push('/users').catch(() => {});
      commit(
        'SET_MESSAGE',
        {
          variant: variants.SUCCESS,
          message: { msg1: i18n.global.t('usersPage.deleteUserSuccess'), msgHasLink: false },
        },
        { root: true }
      );
    } catch (e) {
      commit(
        'SET_MESSAGE',
        { variant: variants.DANGER, message: { msg1: i18n.global.t('usersPage.deleteUserFailed'), msgHasLink: false } },
        { root: true }
      );
      log(e);
    } finally {
      commit('SET_LOADING', false, { root: true });
    }
  },

  async addUserToBusinessAndSendInvitation({ commit }, payload) {
    try {
      commit('SET_LOADING', true, { root: true });
      await businessService.addUserToBusinessAndSendInvitation(payload);
      router.push('/users').catch(() => {});
      commit(
        'SET_MESSAGE',
        { variant: variants.SUCCESS, message: { msg1: i18n.global.t('alerts.inviteEmailSent'), msgHasLink: false } },
        { root: true }
      );
    } catch (e) {
      commit(
        'SET_MESSAGE',
        { variant: variants.DANGER, message: { msg1: i18n.global.t('alerts.inviteEmailError') } },
        { root: true }
      );
      log(e);
    } finally {
      commit('SET_LOADING', false, { root: true });
    }
  },

  // eslint-disable-next-line no-shadow
  async fetchCsRepInformation({ commit, getters }) {
    try {
      const res = await businessService.getCsRepInfo(getters.currentBusinessCrmId);
      commit('SET_CURRENT_BUSINESS_CS_REP_INFO', res.data);
    } catch (e) {
      log(e);
    }
  },
  async getCurrentBusinessHardlinks({ commit }) {
    try {
      const res = await businessService.getBusinessHardlinks();
      // eslint-disable-next-line
      // const res = { data: require('../mock/hardlinks2.json') };
      commit('SET_CURRENT_BUSINESS_HARDLINKS', res.data);
    } catch (error) {
      log(error);
    }
  },
};

const mutations = {
  SET_BUSINESS_DATA: (state, businesses) => {
    state.userBusinesses = businesses;
  },
  SET_CURRENT_BUSINESS: (state, business) => {
    state.currentBusiness = business;
    if (business && typeof window !== 'undefined' && window.Intercom) {
      try {
        window.Intercom('update', {
          av_dashboard_current_business_user_role: business.role,
          av_dashboard_current_business_name: business.name,
          av_dashboard_current_business_id: business.masterBusinessId,
          av_dashboard_current_business_crm_id: business.crmId,
          av_dashboard_current_business_active_products: getActiveProductList(business.avrProductDetails),
        });
      } catch (error) {
        log('Failed to update Intercom:', error);
      }
    }
  },
  SET_CURRENT_INTEGRATION: (state, integration) => {
    state.currentIntegration = integration;
  },
  SET_BUSINESS_USERS: (state, data) => {
    state.users = data;
  },
  SET_SINGLE_BUSINESS_USER: (state, { index, data }) => {
    state.users[index] = data;
  },
  SET_CURRENT_BUSINESS_CS_REP_INFO: (state, data) => {
    state.currentBusinessCSRepInfo = data;
  },
  SET_CURRENT_BUSINESS_HARDLINKS: (state, data) => {
    state.hardlinks = data;
  },
  RESET_STATE: state => {
    const init = cloneDeep(initialState);
    Object.keys(init).forEach(key => {
      state[key] = init[key];
    });
  },
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  getters,
  actions,
  mutations,
};
